import { Suspense, lazy, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { GenericModal, Tabs } from 'src/common';

import { Box, Container, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { companiesApi } from 'src/api/companiesApi';
import { isRequestAbortedError } from 'src/utils/axios/isRequestAbortedError';
import useGenericStyles from 'src/styles/useGenericStyles';

import SimpleBreadCrumb from 'src/components/widgets/BreadCrumb/SimpleBreadCrumb';
import { CustomButton2 } from 'src/components/widgets/buttons/CustomButton2';
import type { TabItem } from 'src/types/tabs';
import { Pricebook } from './pricebook';
// Dynamic imports for tab content components



const About = lazy(() =>
  import('./about/about').then((module) => ({ default: module.About })),
);

const Contacts = lazy(() =>
  import('./contacts/contacts').then((module) => ({
    default: module.Contacts,
  })),
);
const Projects = lazy(() =>
  import('./projects/projects').then((module) => ({
    default: module.Projects,
  })),
);

// Dynamic imports for modal components
const AddProduct = lazy(() =>
  import('./projects/add-product/add-product').then((module) => ({ default: module.AddProduct })),
);
const AddEditContact = lazy(() =>
  import('src/components/add-edit-contact').then((module) => ({
    default: module.AddEditContact,
  })),
);


// Loading component for Suspense fallback
export const TabContentLoader = (): JSX.Element => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px">
    <Typography>Loading...</Typography>
  </Box>
);

// ----------------------------------------------------
// All API functions are here
export const fetchCompanyDetails = async ({ companyId, setLoading }: { companyId: string, setLoading?: React.Dispatch<React.SetStateAction<boolean>> }) => {
  if (!companyId) {
    return null;
  }

  if (setLoading) setLoading(true);
  try {
    const response = await companiesApi.getCompanyById(companyId);
    const data = response?.data?.data || response?.data || response;
    return data || null;
  } catch (error) {
    if (!isRequestAbortedError(error)) {
      console.error('Error fetching company details:', error);
    }
    return null;
  } finally {
    if (setLoading) setLoading(false);
  }
};

export const fetchCompanyOverview = async ({ companyId }: { companyId: string }) => {
  try {
    const response = await companiesApi.getCompanyOverviewById(companyId);
    const data = response?.data?.data || response?.data || response;
    return data || null;
  } catch (error) {
    console.error('Error fetching company overview:', error);
    return null;
  }
}

export const CompanyDetails = (): JSX.Element => {
  const classes = useGenericStyles();
  const navigate = useNavigate();
  const theme = useTheme();


  const [activeTab, setActiveTab] = useState(0);
  const params = useParams();
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isAddEditContactOpen, setIsAddEditContactOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState<any>({});
  const [companyOverview, setCompanyOverview] = useState<any>(null);

  // const { data: companyDetails } = useGetSingleCompaniesQuery({
  //   companyId: params?.companyId,
  // });
  // const { data: companyOverview } = useGetCompanyOverviewByIdQuery({
  //   companyId: params?.companyId,
  // });

  // const permissions = usePermissions();
  // Map tab indices to hash values
  const tabToHash = [
    'projects',
    'about',
    'contacts',
    'pricebook',
    // 'consulting-units',
    // 'client-settings',
    // 'general-settings',
  ];
  const hashToTab = tabToHash.reduce(
    (acc, hash, index) => {
      acc[hash] = index;
      return acc;
    },
    {} as Record<string, number>,
  );

  // Initialize active tab from URL hash on component mount
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && hashToTab[hash] !== undefined) {
      setActiveTab(hashToTab[hash]);
    }
  }, [hashToTab]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string | number | undefined,
  ): void => {
    const tabIndex = Number(newValue);
    setActiveTab(tabIndex);
    // Update URL hash when tab changes
    window.location.hash = tabToHash[tabIndex];
  };

  const handleCreateProjectModalOpen = (): void => setIsProjectModalOpen(true);
  const handleCreateProjectModalClose = (): void => setIsProjectModalOpen(false);

  const handleContactClose = (): void => setIsAddEditContactOpen(false);
  const tabs: TabItem[] = [
    {
      label: 'Projects',
    },
    {
      label: 'About',
    },
    {
      label: 'Contacts',
    },
    {
      label: 'Pricebook',
    },
    // {
    //   label: 'Consulting Units',
    // },
    // {
    //   label: 'Client Settings',
    // },
    // {
    //   label: 'General Settings',
    // },
  ].filter(Boolean) as TabItem[];


  // This is user Panel version of breadcrumb
  // This is user Panel version of breadcrumb
  const breadcrumbData = {
    links: [
      {
        name: 'Clients',
        to: '/clients',
      },
      {
        name: companyDetails?.name,
        to: `/clients/${companyDetails?.id}`,
      },
    ],
  };


  const handleProjectNavigation = (row: any): void => {
    // if (permissions.COMPANY_PROJECT.GET_BY_ID) {
    navigate(`/clients/${params?.companyId}/projects/${row?.id}`);
    // }
  };


  useEffect(() => {
    if (params?.companyId) {
      const loadCompanyData = async () => {
        const detailsData = await fetchCompanyDetails({ companyId: params?.companyId, setLoading });
        const overviewData = await fetchCompanyOverview({ companyId: params?.companyId });

        if (detailsData) setCompanyDetails(detailsData);
        if (overviewData) setCompanyOverview(overviewData);
      };

      loadCompanyData();
    }
  }, [params?.companyId]);



  // Render content based on the active tab
  const renderTabContent = (): JSX.Element | null => {
    switch (activeTab) {
      case 0:
        return (
          <Suspense fallback={<TabContentLoader />}>
            <Projects
              organizationsId={params?.companyId ?? ''}
              handleProjectNavigation={handleProjectNavigation}
            />
          </Suspense>
        );
      case 1:
        return (
          <Suspense fallback={<TabContentLoader />}>
            <About
              companyData={companyDetails}
              companyOverview={companyOverview}
              onCompanyUpdated={(updated) => {
                setCompanyDetails(updated);
              }}
            />
          </Suspense>
        );
      case 2:
        return (
          <Suspense fallback={<TabContentLoader />}>
            <Contacts companyId={companyDetails?.id} />
          </Suspense>
        );
      case 3:
        return (
          <Suspense fallback={<TabContentLoader />}>
            <Pricebook organizationId={companyDetails?.id} />
          </Suspense>
        );
      // case 4:
      //   return (
      //     <Suspense fallback={<TabContentLoader />}>
      //       <ConsultingUnits companyId={companyDetails?.id} />
      //     </Suspense>
      //   );
      // case 5:
      //   return (
      //     <Suspense fallback={<TabContentLoader />}>
      //       <ClientSettings companyId={companyDetails?.id} />
      //     </Suspense>
      //   );
      // case 6:
      //   return (
      //     <Suspense fallback={<TabContentLoader />}>
      //       <GeneralSettings companyData={companyDetails} />
      //     </Suspense>
      //   );
      default:
        return null;
    }
  };


  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
      >
        <Typography variant="h5">Loading company details...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Container maxWidth={false} className={classes.container}>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box display="flex" flexDirection="column">
              <SimpleBreadCrumb data={breadcrumbData} />
              <Typography variant="h4" sx={{ marginTop: '10px' }}>
                {companyDetails?.name}
              </Typography>
            </Box>

            <Box display="flex" gap={1}>
              {/* <CustomButton2
                backgroundColor={theme.palette.primary.main}
                className={classes.button}
              // onClick={handleOpenReleaseVersionModal}
              >
                {'New Release'}
              </CustomButton2> */}


              {/* {permissions.COMPANY_PROJECT.CREATE && ( */}
              <CustomButton2
                backgroundColor={theme.palette.primary.main}
                className={classes.button}
                onClick={handleCreateProjectModalOpen}
              >
                <AddIcon
                  sx={{
                    marginRight: '5px',
                  }}
                />
                Create New Project
              </CustomButton2>
              {/* )} */}
              {activeTab === 2 && (
                <CustomButton2
                  backgroundColor={theme.palette.primary.main}
                  className={classes.button}
                  onClick={() => setIsAddEditContactOpen(true)}
                >
                  <AddIcon
                    sx={{
                      marginRight: '5px',
                    }}
                  />
                  Add Contact
                </CustomButton2>
              )}
            </Box>
          </Box>
          <Tabs
            tabs={tabs}
            handleTabChange={handleTabChange}
            activeTab={activeTab}
          />

          <Box sx={{ mt: 1 }}>{renderTabContent()}</Box>

          <GenericModal
            open={isProjectModalOpen}
            onClose={handleCreateProjectModalClose}
            title="Add Project">
            <Suspense fallback={<TabContentLoader />}>
              <AddProduct
                companyId={params?.companyId ?? ''}
                onClose={() => {
                  setIsProjectModalOpen(false);
                  // Refetch the data here
                  window.location.reload();
                }}
              />
            </Suspense>
          </GenericModal>

          <GenericModal
            open={isAddEditContactOpen}
            onClose={handleContactClose}
            title="Add Contact">
            <Suspense fallback={<TabContentLoader />}>
              <AddEditContact
                companyId={params?.companyId ?? ''}
                onClose={() => {
                  setIsAddEditContactOpen(false);
                }}
              />
            </Suspense>
          </GenericModal>

        </Box>

      </Container>
    </>

  );
};
